import Axios from 'axios';
import i18n from '@i18next/i18n';
import { ptToCanvas } from '../../../independent/unit';
import { tdw } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/tdw-report';
import { ImageResource } from '@tencent/alloy-renderer';
import ImageOriginalSizeWithDPI from '../original-size-with-dpi';

/**
 * 图片格式转换 - Blob => Base64
 * @param {Blob} blob 数据类型
 * @return {Promise}
 *      @resolve {String|Null} 如有有结果，则返回base64字符串，如转换失败，则返回null
 */
export function blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve) => {
        const reader = new FileReader();

        reader.onload = (e) => {
            const result = e.target?.result as string || '';
            resolve(result);
        };

        reader.onerror = () => {
            resolve('');
        };

        reader.readAsDataURL(blob);
    });
}
/**
 * @param image 图片资源池中的图片
 * @param sizeCompression 图像的大小压缩，0-1，1为不压缩
 * @param quality 图像的质量压缩，0-1，1为不压缩
 */
export function getImageBase64Sync(image: ImageResource, sizeCompression?: number, quality?: number) {
    const canvas = document.createElement('canvas');
    const width = image.width * (sizeCompression || 1);
    const height = image.height * (sizeCompression || 1);
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    ctx?.drawImage(image.data as CanvasImageSource, 0, 0, width, height);
    const dataURL = canvas.toDataURL('image/png', quality);
    return dataURL;
}

export async function imageToBase64(url) {
    return new Promise((resolve, reject) => {
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        const img = new Image();

        img.onload = function () {
            canvas.width = img.width;
            canvas.height = img.height;
            context?.drawImage(img, 0, 0);
            const URLData = canvas.toDataURL('image/png');
            resolve(URLData);
        };

        img.onerror = () => {
            reject(null);
        };

        img.crossOrigin = 'anonymous';
        img.alt = '';
        // 为图片后缀添加随机数，防止从缓存中拿取图片数据
        img.src = `${url}&random=${Math.random()}`;
    });
}

export function getImageBase64(imageSdkData): Promise<string> {
    const imageUrl = imageSdkData.image?.src || imageSdkData.image?.blip?.src;
    return this.imageUrlToBase64(imageUrl);
}

export function imageUrlToBase64(imageUrl: string): Promise<string> {
    return Axios.get(imageUrl, { responseType: 'blob' })
        .then(response => blobToBase64(response.data))
        .catch(() => imageUrl);
}

export function imageUrlToBlob(imageUrl: string): Promise<string> {
    return Axios.get(imageUrl, { responseType: 'blob' })
        .then(response => response.data)
        .catch(() => imageUrl);
}
/**
 * 计算图片的初始信息
 */
export function getNextSize(img, slideWH) {
    let width;
    let height;
    const originalSize = ImageOriginalSizeWithDPI.getImageOriginalSize(img.src);
    if (originalSize) {
        ({ width, height } = originalSize);
    } else {
        width = ptToCanvas(img.width);
        height = ptToCanvas(img.height);
    }
    const { slideWidth, slideHeight } = slideWH;

    let nextWidth = width;
    let nextHeight = height;
    let ratio;
    if (width > slideWidth || height > slideHeight) {
        const ratioX = slideWidth / width;
        const ratioY = slideHeight / height;
        ratio = ratioX < ratioY ? ratioX : ratioY;
        nextWidth = width * ratio;
        nextHeight = height * ratio;
    }
    return { nextWidth, nextHeight, ratio };
}

/**
 * 根据 URL 判断图片类型
 * @param {*} url 图片 url
 */
export function getImageTypeByUrl(url) {
    const imageNameArr = url.split('?').shift()
        .split('/')
        .pop()
        .split('.');
    return imageNameArr.length === 2 ? imageNameArr[1] : '';
}

/**
 * 获取错误详情，进行日志上报
 */
export function fetchErrorTips(errorCode) {
    let errorTips = i18n.t('未成功插入图片，请稍后再试。');
    switch (errorCode) {
        case 90003:
        case 90011:
            errorTips = i18n.t('未成功插入图片，请稍后再试。');
            break;
        case 90001:
        case 10003:
            errorTips = i18n.t('暂无权限上传图片，请重新登录后再试。');
            break;
        case 90008:
            errorTips = i18n.t('上传图片失败，图片损坏或不支持该格式。');
            break;
        case 90010:
            errorTips = i18n.t('图片体积太大，请压缩后再试。');
            break;
    }
    return errorTips;
}

/**
 * 上传图片错误上报
 * @param {*} info 上报的信息
 */
export function uploadImageBase64Error(info) {
    window.log.error(`[UploadGIFFrameImages] ${info || ''}`);
    tdw({
        opername: 'quality_slide',
        module: 'image',
        action: 'upload',
        ver6: info || '',
    });
}

/**
 * 上传 Base64 格式的图片，简单版的上传图片。
 * 增加日志上报信息，静默失败，不会出现 error 提示。
 */
export function uploadImageBase64(URLData) {
    const { clientVars } = window;
    const url = `/v1/image/upload?globalPadId=${clientVars.globalPadId}`;

    return new Promise((resolve, reject) => {
        if (!URLData) reject('URLData is null');

        const urlDataBlob = new Blob([URLData]);
        const form = new FormData();
        form.append('file', urlDataBlob);
        form.append('isBase64', '1');

        const xhr = new XMLHttpRequest();
        xhr.open('POST', url, true);

        xhr.onload = () => {
            if (xhr.status === 200 && xhr.response) {
                try {
                    const data = JSON.parse(xhr.response);
                    const { url } = data;
                    if (url && data.retcode === 0) {
                        resolve(url);
                    } else {
                        const errorTips = `upload Image error: ${fetchErrorTips(data.retcode)}`;
                        uploadImageBase64Error(errorTips);
                        reject(errorTips);
                    }
                } catch (error) {
                    const errorTips = `upload Image error: ${xhr.response}`;
                    uploadImageBase64Error(errorTips);
                    reject(errorTips);
                }
            } else {
                const errorTips = `upload Image error: ${xhr.response}`;
                uploadImageBase64Error(errorTips);
                reject(errorTips);
            }
        };

        xhr.onerror = () => {
            const errorTips = `upload Image error: ${xhr.statusText}`;
            uploadImageBase64Error(errorTips);
            reject(errorTips);
        };
        xhr.ontimeout = () => {
            const errorTips = `upload Image timeout: ${xhr.statusText}`;
            uploadImageBase64Error(errorTips);
            reject(errorTips);
        };
        xhr.send(form);
    });
}

export default {};
